/**
 * เลเวลสกิลและแต้มสกิล — กฎกลางที่ทั้งสองฝั่งใช้
 *
 * ตาม GDD หัวข้อ "กติกาสกิล": ได้ 1 แต้มสกิลต่อ Job Level, สกิลละ 1–10 เลเวล
 * สกิลขั้นถัดไปต้องมีสกิลพื้นฐานถึงเลเวลที่กำหนด (skill tree แบบง่าย)
 * และรีเซ็ตได้ฟรี 1 ครั้งก่อน Base Lv 40
 *
 * **อยู่ใน shared เพราะ client ต้องคิดตามให้ตรงกัน** หน้าต่างสกิลโชว์ดาเมจ MP
 * และระยะเวลาของเลเวลถัดไปก่อนกดลงแต้ม ถ้าคิดคนละสูตร ตัวเลขที่เห็นกับที่ออกจริงจะไม่ตรง
 * server เป็นคนตัดสินว่าลงแต้มได้จริงไหม (server/src/rooms/FieldRoom.ts)
 *
 * ทุกฟังก์ชันเป็น pure function ไม่สุ่มและไม่อ่าน state
 */
import { WEAPON_TYPE_NAMES } from "./types.js";
import { CLASSES } from "./data.js";
/** เลเวลสูงสุดของสกิล ถ้าสกิลนั้นไม่ได้กำหนด `maxLv` เอง (GDD: 1–10) */
export const SKILL_MAX_LV = 10;
/**
 * ตัวคูณที่เพิ่มต่อเลเวลสกิล นับจาก Lv 1
 *
 * ค่าเริ่มต้นสำหรับลองเล่น ปรับได้ที่นี่ที่เดียว ทั้งสองฝั่งเปลี่ยนตาม
 * - ดาเมจ +10% ต่อเลเวล (Lv 10 = 190% ของ Lv 1)
 * - ค่าบัฟ (DEF คริ บล็อก ฮีล ความเร็วตี) +10% ต่อเลเวล
 * - ระยะเวลาบัฟและสถานะ +5% ต่อเลเวล
 * - MP +5% ต่อเลเวล — แรงขึ้นต้องจ่ายแพงขึ้นบ้าง แต่น้อยกว่าที่แรงขึ้น
 * - คูลดาวน์ไม่เปลี่ยน ไม่งั้นสกิลเลเวลสูงจะกดรัวจนจังหวะการต่อสู้เสีย
 */
export const SKILL_POWER_PER_LV = 0.1;
export const SKILL_BUFF_PER_LV = 0.1;
export const SKILL_DURATION_PER_LV = 0.05;
export const SKILL_MP_PER_LV = 0.05;
const round2 = (n) => Math.round(n * 100) / 100;
/** เงื่อนไขสกิลพื้นฐานทั้งหมดของสกิลนี้ (รับทั้งรูปเดี่ยวเดิมและรายการ) ไม่มี = รายการว่าง */
export function skillReqs(def) {
    if (!def.req)
        return [];
    return Array.isArray(def.req) ? def.req : [def.req];
}
/** เลเวลสูงสุดของสกิลนี้ */
export function skillMaxLv(def) {
    return def.maxLv ?? SKILL_MAX_LV;
}
/**
 * สำเนาของสกิลที่ปรับค่าตามเลเวลแล้ว
 *
 * **โค้ดที่คิดผลสกิลรับสำเนานี้ไปใช้แทนนิยามดิบได้เลย** ฟิลด์ทุกตัวชื่อเดิม
 * server จึงไม่ต้องแก้โค้ดดาเมจ บัฟ หรือสถานะ แค่เปลี่ยนตัวที่ส่งเข้าไป
 * ไม่แก้ object ต้นฉบับ เพราะนิยามจาก JSON ใช้ร่วมกันทั้งห้อง
 */
export function skillAtLevel(def, lv) {
    const level = Math.max(1, Math.min(skillMaxLv(def), Math.floor(lv) || 1));
    const step = level - 1;
    if (step === 0)
        return def;
    const pow = 1 + SKILL_POWER_PER_LV * step;
    const buf = 1 + SKILL_BUFF_PER_LV * step;
    const dur = 1 + SKILL_DURATION_PER_LV * step;
    const out = {
        ...def,
        power: round2(def.power * pow),
        mp: Math.round(def.mp * (1 + SKILL_MP_PER_LV * step)),
    };
    if (def.status) {
        out.status = { ...def.status, duration: round2(def.status.duration * dur) };
    }
    if (def.buff) {
        const b = def.buff;
        out.buff = { ...b, duration: round2(b.duration * dur) };
        if (b.flatDef !== undefined)
            out.buff.flatDef = Math.round(b.flatDef * buf);
        if (b.flatCrit !== undefined)
            out.buff.flatCrit = round2(b.flatCrit * buf);
        if (b.flatBlock !== undefined)
            out.buff.flatBlock = round2(b.flatBlock * buf);
        if (b.healPct !== undefined)
            out.buff.healPct = round2(b.healPct * buf);
        // ความเร็วตีเป็นตัวคูณ เพิ่มเฉพาะส่วนที่เกิน 1 ไม่งั้น 1.2 จะกลายเป็น 2.28 ที่ Lv 10
        if (b.aspdMul !== undefined)
            out.buff.aspdMul = round2(1 + (b.aspdMul - 1) * buf);
    }
    return out;
}
/* ---------- แต้มสกิล ---------- */
/** แต้มสกิลทั้งหมดที่ได้จาก Job Level (ได้ 1 แต้มทุกครั้งที่ Job Level ขึ้น) */
export function skillPointsEarned(jobLevel) {
    return Math.max(0, Math.floor(jobLevel) - 1);
}
/**
 * สกิลที่ได้ Lv 1 ฟรีตอนเริ่ม คือสกิลแรกของอาชีพ
 *
 * ไม่งั้นตัวละครใหม่เข้าเกมมาแล้วไม่มีสกิลใช้เลยจนกว่าจะขึ้น Job 2
 */
export function freeSkillId(cls) {
    return cls.skills[0]?.id;
}
/** เลเวลสกิลตอนเริ่มต้น (หรือหลังรีเซ็ต) */
export function startingSkillLevels(cls) {
    const free = freeSkillId(cls);
    return free ? { [free]: 1 } : {};
}
/** อ่านเลเวลสกิลจาก Map, MapSchema หรือ object */
function levelOf(levels, id) {
    const anyLv = levels;
    const v = typeof anyLv.get === "function" ? anyLv.get(id) : levels[id];
    return Number(v ?? 0) || 0;
}
/**
 * แต้มที่ใช้ไปแล้ว ไม่นับ Lv 1 ฟรีของสกิลแรก
 * สกิลที่ไม่มีในอาชีพนี้ไม่ถูกนับ (ข้อมูลเก่าที่ค้างในฐานข้อมูล)
 */
export function skillPointsSpent(cls, levels) {
    const free = freeSkillId(cls);
    let spent = 0;
    for (const s of cls.skills) {
        const lv = levelOf(levels, s.id);
        spent += s.id === free ? Math.max(0, lv - 1) : lv;
    }
    return spent;
}
/** แต้มที่เหลือให้ลง */
export function skillPointsLeft(cls, levels, jobLevel) {
    return skillPointsEarned(jobLevel) - skillPointsSpent(cls, levels);
}
export const LEARN_TEXT = {
    unknown: "อาชีพนี้ไม่มีสกิลนี้",
    max: "สกิลนี้เลเวลสูงสุดแล้ว",
    points: "แต้มสกิลไม่พอ",
    job: "Job Level ยังไม่ถึง",
    req: "ต้องอัปสกิลพื้นฐานก่อน",
};
/**
 * ลงแต้มเพิ่ม 1 เลเวลให้สกิลนี้ได้ไหม คืน undefined ถ้าได้
 *
 * เงื่อนไข: เป็นสกิลของอาชีพนี้ · ยังไม่เต็ม · มีแต้มเหลือ · Job Level ถึง `job`
 * · และถ้ามี `req` สกิลพื้นฐานต้องถึงเลเวลที่กำหนด
 */
export function learnDenial(cls, levels, skillId, jobLevel) {
    const def = cls.skills.find((s) => s.id === skillId);
    if (!def)
        return "unknown";
    if (levelOf(levels, skillId) >= skillMaxLv(def))
        return "max";
    if (jobLevel < def.job)
        return "job";
    if (skillReqs(def).some((r) => levelOf(levels, r.skill) < r.lv))
        return "req";
    if (skillPointsLeft(cls, levels, jobLevel) < 1)
        return "points";
    return undefined;
}
/**
 * อ่านเลเวลสกิลจากข้อมูลภายนอก (ฐานข้อมูล) ให้อยู่ในกติกา
 *
 * - ตัดสกิลที่ไม่มีในอาชีพนี้ทิ้ง (สกิลที่ถูกถอดออกจากเกม)
 * - หนีบเลเวลไม่ให้เกินเพดาน
 * - สกิลแรกต้องมีอย่างน้อย Lv 1 (ของฟรี)
 * - ถ้าแต้มที่ใช้เกินแต้มที่มี (เช่นปรับกติกาแล้ว) **รีเซ็ตกลับค่าเริ่มต้น**
 *   ให้ผู้เล่นลงใหม่ ดีกว่าปล่อยให้มีแต้มติดลบ
 * - `uncapped` = ข้ามการตรวจแต้ม (บัญชี GM ที่ให้สกิลเต็มทุกตัวไว้ทดสอบ) เพดานเลเวลรายสกิลยังหนีบเหมือนเดิม
 */
export function sanitizeSkillLevels(cls, raw, jobLevel, uncapped = false) {
    const out = startingSkillLevels(cls);
    for (const s of cls.skills) {
        const lv = Math.floor(Number(raw?.[s.id] ?? 0));
        if (lv > 0)
            out[s.id] = Math.max(out[s.id] ?? 0, Math.min(skillMaxLv(s), lv));
    }
    const base = baseClassSkillLevels(cls, raw);
    if (!uncapped && skillPointsSpent(cls, out) > skillPointsEarned(jobLevel)) {
        return { ...base, ...startingSkillLevels(cls) };
    }
    return { ...base, ...out };
}
/**
 * สกิลอาชีพขั้นต้นที่อาชีพขั้นสูงพกติดตัวมา (หนีบเพดานแล้ว)
 *
 * แต้มสกิลของอาชีพขั้นสูงนับใหม่จาก Job 1 จึง**ไม่นับ**สกิลชุดนี้ในแต้มที่ใช้
 * และลงเพิ่มไม่ได้แล้ว (learnDenial ดูแค่ cls.skills) แต่ยังร่ายได้ตามเลเวลที่ลงไว้ก่อนเลื่อนขั้น
 */
export function baseClassSkillLevels(cls, raw) {
    const out = {};
    if (cls.tier !== 2 || !cls.baseClass || !raw)
        return out;
    for (const s of CLASSES[cls.baseClass]?.skills ?? []) {
        const lv = Math.floor(levelOf(raw, s.id));
        if (lv > 0)
            out[s.id] = Math.min(skillMaxLv(s), lv);
    }
    return out;
}
/** Base Level ที่ยังรีเซ็ตสกิลฟรีได้ (GDD: รีเซ็ตฟรี 1 ครั้งก่อน Lv 40) */
export const FREE_SKILL_RESET_BEFORE = 40;
/**
 * ท่าถือตรงกับที่สกิลต้องการไหม (`SkillDef.needs`) สกิลที่ไม่ได้ระบุร่ายได้ทุกท่า
 * server ใช้ตัดสินตอนร่าย client ใช้แสดงช่องสกิลเป็นสีจางก่อนกด
 */
export function weaponNeedMet(def, held) {
    const n = def.needs;
    if (!n)
        return true;
    if (n.weapon?.length && !(held.weapon && n.weapon.includes(held.weapon)))
        return false;
    if (n.offhand?.length && !(held.offhand && n.offhand.includes(held.offhand)))
        return false;
    return true;
}
/** ข้อความบอกท่าถือที่สกิลต้องการ เช่น "ต้องถือ ดาบมือเดียว + โล่" ไม่มีเงื่อนไข = "" */
export function describeNeeds(def) {
    const n = def.needs;
    if (!n)
        return "";
    const part = (ts) => ts?.length ? ts.map((t) => WEAPON_TYPE_NAMES[t] ?? t).join("/") : "";
    const bits = [part(n.weapon), part(n.offhand)].filter(Boolean);
    return bits.length ? `ต้องถือ ${bits.join(" + ")}` : "";
}
/** รวมผลเสริมสกิลจากหลายแหล่ง (พลัง/ระยะเวลาบวกกัน · MP/คูลดาวน์คูณกัน) ไม่แก้ต้นฉบับ */
export function mergeSkillMods(...sources) {
    const out = {};
    for (const src of sources) {
        for (const [id, m] of Object.entries(src ?? {})) {
            const o = (out[id] ??= {});
            if (m.power)
                o.power = (o.power ?? 0) + m.power;
            if (m.duration)
                o.duration = (o.duration ?? 0) + m.duration;
            if (m.mpMul)
                o.mpMul = (o.mpMul ?? 1) * m.mpMul;
            if (m.cdMul)
                o.cdMul = (o.cdMul ?? 1) * m.cdMul;
        }
    }
    return out;
}
/**
 * ใช้ผลเสริมสกิลกับสำเนาที่ได้จาก `skillAtLevel` คืนสำเนาใหม่ ชื่อฟิลด์เดิมทุกตัว
 * **ไม่แก้ object ต้นฉบับ** (มาจาก JSON ที่ทุกคนใช้ร่วมกัน)
 */
export function applySkillMods(def, mod) {
    if (!mod)
        return def;
    const out = { ...def };
    if (mod.power) {
        out.power = def.power * (1 + mod.power);
        // สกิลรักษามีพลัง 0 และฟื้นตาม healPct — "พลัง" ของสกิลพวกนี้คือปริมาณที่รักษา
        if (def.buff?.healPct)
            out.buff = { ...def.buff, healPct: round2(def.buff.healPct * (1 + mod.power)) };
    }
    if (mod.mpMul)
        out.mp = Math.max(0, Math.round(def.mp * mod.mpMul));
    if (mod.cdMul)
        out.cd = Math.round(def.cd * mod.cdMul * 10) / 10;
    if (mod.duration) {
        const k = 1 + mod.duration;
        if (out.buff)
            out.buff = { ...out.buff, duration: Math.round(out.buff.duration * k * 10) / 10 };
        if (def.status)
            out.status = { ...def.status, duration: Math.round(def.status.duration * k * 10) / 10 };
    }
    return out;
}
/** ชื่อไทยของสกิลจาก id (ค้นทุกอาชีพ) ใช้ในคำบรรยายของ ไม่เจอคืน id */
export function skillName(id) {
    for (const c of Object.values(CLASSES)) {
        const sk = c.skills.find((x) => x.id === id);
        if (sk)
            return sk.th;
    }
    return id;
}
/** คำบรรยายผลเสริมสกิล สร้างจากข้อมูล */
export function describeSkillMods(mods) {
    const out = [];
    const pct = (x) => `${Math.round(x * 100)}%`;
    for (const [id, m] of Object.entries(mods ?? {})) {
        const bits = [];
        if (m.power)
            bits.push(`พลัง +${pct(m.power)}`);
        if (m.duration)
            bits.push(`ระยะเวลา +${pct(m.duration)}`);
        if (m.mpMul)
            bits.push(`MP -${pct(1 - m.mpMul)}`);
        if (m.cdMul)
            bits.push(`คูลดาวน์ -${pct(1 - m.cdMul)}`);
        if (bits.length)
            out.push(`${skillName(id)}: ${bits.join(" ")}`);
    }
    return out;
}
//# sourceMappingURL=skills.js.map